/**
 * ============================================================================
 * useSignSnapshotMutation — Phase 6b frontend (Snapshot Signing Tray)
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: POST /api/v1/quality-metrics/snapshots/{id}/sign — record the QA
 *          Director / Site Quality Head signature on a monthly quality-metric
 *          snapshot. The signed content_hash becomes immutable in the
 *          audit-svc store.
 *
 * REGULATORY:
 *   Signing requires a phishing-resistant 2FA challenge id (`auth_method:
 *   WEBAUTHN`) plus a free-text attestation. The server re-derives the
 *   content_hash from the persisted metrics — if it differs from the value
 *   the user is signing, the request is rejected (defence against
 *   client-side substitution).
 * ============================================================================
 */

import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { apiFetch, newIdempotencyKey } from '../../api/client';
import type { ClientAssertionResponse } from '../../auth/webauthn';
import type { QualitySnapshot } from './useSnapshotsQuery';

export interface SignSnapshotInput {
  snapshotId: string;
  /** Hex SHA-256 the user is acknowledging; the server cross-checks. */
  contentHash: string;
  attestationText: string;
  /** 2026-05-28 C1: encoded WebAuthn assertion from navigator.credentials.get(). */
  webauthnAssertion?: ClientAssertionResponse;
}

export function useSignSnapshotMutation(): UseMutationResult<
  QualitySnapshot,
  Error,
  SignSnapshotInput
> {
  return useMutation<QualitySnapshot, Error, SignSnapshotInput>({
    mutationFn: ({ snapshotId, contentHash, attestationText, webauthnAssertion }) =>
      apiFetch<QualitySnapshot>(
        `/api/v1/quality-metrics/snapshots/${encodeURIComponent(snapshotId)}/sign`,
        {
          method: 'POST',
          body: {
            // signature_bundle_id used to be a separate row in a sig-bundle
            // table; we send the snapshot id itself now so the server-side
            // validation has a non-null value and the WebAuthn ceremony
            // proves who signed it.
            signature_bundle_id: snapshotId,
            content_hash: contentHash,
            auth_method: 'WEBAUTHN',
            attestation_text: attestationText,
            webauthn_assertion: webauthnAssertion ?? null,
          },
          idempotencyKey: newIdempotencyKey(),
        },
      ),
  });
}
